import { FloatingPortal } from '@floating-ui/react';
import { Button, Dialog, Loader, OutreachEditorRef } from '@recruit-robin/robin-theme/web-components';
import { CheckCircleBroken, XCircle } from '@recruit-robin/robin-theme/web-icons';
import { motion } from 'framer-motion';
import { useCallback, useContext, useEffect, useState } from 'react';

import type { Outreach } from 'clients/OutreachTemplateclient/OutreachTemplateClient.types';
import type { ContactCandidateData } from 'hooks/shared/useContactCandidateMutation/useContactCandidateMutation.types';
import type { IndeedJob } from 'shared/legacy/hooks/useBrowserExtension/indeed/indeed.types';
import type { IndeedProject } from 'shared/legacy/hooks/useBrowserExtension/indeed/useIndeedProjectListQuery';
import type { OutreachPanelPageInfo } from './OutreachModalContext/OutreachModalContext';
import type { OutreachModalErrorType } from './hooks/useOutreachModalErrors';

import { useGTM } from 'hooks/gtm';
import { useContactCandidateMutation } from 'hooks/shared/useContactCandidateMutation/useContactCandidateMutation';
import { EventBus } from 'services/event-bus/EventBus';
import { CandidateEvent } from 'services/event-bus/EventBus.types';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { SnackbarContext } from 'shared/legacy/contexts/SnackbarContext/SnackbarContext';
import { getIndeedCandidateEngagementErrorMessage } from 'shared/legacy/exceptions/getIndeedCandidateEngagementErrorMessage';
import { useIndeedReply } from 'shared/legacy/hooks/useBrowserExtension/indeed/useIndeedReply';
import { useCandidateNotesMutation } from 'shared/legacy/hooks/useCandidateNotesMutation';
import { useCurrentUserQuery } from 'shared/legacy/hooks/useCurrentUserQuery';
import { useFeatureToggles } from 'shared/legacy/hooks/useFeatureToggles';
import { useGetJobImprovedQuery } from 'shared/legacy/hooks/useGetJobImprovedQuery';
import { useUserEmailSignatureQuery } from 'shared/legacy/hooks/useUserEmailSignatureQuery';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from 'stores/global-dialogs/hooks/useGlobalDialog';
import { useRobinJrStore } from 'stores/robin-jr/robin-jr.store';
import { OutreachType } from 'types/outreach';
import { calculateLevenshteinDistance } from 'utils/calculateLevenshteinDistance';
import { calculateTextSimilarity } from 'utils/calculateTextSimilarity';
import { openWhatsappWeb } from 'utils/openWhatsappWeb';
import { htmlToPlainText } from 'utils/shared/htmlToPlainText';
import { OutreachOrigin } from '@/shared/lib/outreach-origin';
import { OutreachPanelPage } from '../OutreachPanel/utils/OutreachPanelPage';
import { useStyles } from './OutreachModal.styles';
import { OutreachModalProps } from './OutreachModal.types';
import { OutreachModalContext, OutreachModalContextState } from './OutreachModalContext';
import { initialState } from './OutreachModalContext/OutreachModalContext';
import { OutreachPanelLazy } from './OutreachPanel.lazy';

const WIDTH_EXPANDED_TOOLBAR = 651;
const AI_MODIFIED_TEXT_SIMILARITY_THRESHOLD = 0.7;

export const OutreachModal = ({ candidate, type: initialType }: OutreachModalProps) => {
  const { createSnackbar } = useContext(SnackbarContext);
  const { isReply, metadata } = useIndeedReply(candidate);
  const { dictionary } = useContext(LocalizationContext);
  const candidateOutreachModal = useGlobalDialog(GlobalDialogType.CandidateOutreach);
  const classes = useStyles();
  const [attachments, setAttachments] = useState(initialState.attachments);
  const [page, setPage] = useState(initialState.page);
  const [pageInfo, setPageInfo] = useState<OutreachPanelPageInfo>(initialState.pageInfo);
  const [selectedTemplate, setSelectedTemplate] = useState<Outreach | null>(initialState.selectedTemplate);
  const [selectedIndeedProject, setSelectedIndeedProject] = useState<IndeedProject | null>(
    initialState.selectedIndeedProject,
  );
  const [selectedIndeedJob, setSelectedIndeedJob] = useState<IndeedJob | null>(initialState.selectedIndeedJob);
  const [editorSubjectRef, setEditorSubjectRef] = useState<OutreachEditorRef | null>(null);
  const [editorMessageRef, setEditorMessageRef] = useState<OutreachEditorRef | null>(null);
  const [unSubmitedLabels, setUnSubmitedLabels] = useState<string[]>([]);
  const [errors, setErrors] = useState<OutreachModalErrorType[]>([]);

  const baseData = {
    activeJobBoard: candidate.activeJobBoard,
    campaignId: candidate.campaignId,
    candidateStatus: candidate.status,
  } as const;

  const selectedVacancy = useRobinJrStore((s) => s.selectedVacancy);
  const isRobinJrFollowUpEnabled = useRobinJrStore((s) => s.isRobinJrFollowUpEnabled);
  const isRobinJrEnabled = useRobinJrStore((s) => s.isRobinJrEnabled);
  const hasInsufficientInfoError = useRobinJrStore((s) => s.hasInsufficientInfoError);
  const resetRobinJrStore = useRobinJrStore((s) => s.reset);
  const aiOriginalMessage = useRobinJrStore((s) => s.aiOriginalMessage);
  const setAiOriginalMessage = useRobinJrStore((s) => s.setAiOriginalMessage);
  const gtm = useGTM();

  const { data: currentUser } = useCurrentUserQuery();

  const contactCandidateMutation = useContactCandidateMutation();

  const shouldScheduleFollowUp =
    isRobinJrFollowUpEnabled && isRobinJrEnabled && !hasInsufficientInfoError && !!aiOriginalMessage;

  const candidateNotesMutation = useCandidateNotesMutation();

  const [showWhatsappWebModal, setShowWhatsappWebModal] = useState(false);
  const [isSendingWhatsappMessage, setIsSendingWhatsappMessage] = useState(false);

  const [type, setType] = useState(initialType);
  useEffect(() => setType(initialType), [initialType]);
  const emailSignature = useUserEmailSignatureQuery({ enabled: type === OutreachType.Email });

  const { isFeatureToggleEnabled } = useFeatureToggles();

  useEffect(() => {
    return () => {
      setPage(initialState.page);
      setPageInfo(initialState.pageInfo);
      setSelectedTemplate(initialState.selectedTemplate);
      setSelectedIndeedProject(initialState.selectedIndeedProject);
      setSelectedIndeedJob(initialState.selectedIndeedJob);
      resetRobinJrStore();
    };
  }, []);

  useEffect(() => {
    if (currentUser) {
      setPageInfo((pageInfo) => ({
        ...pageInfo,
        outreach: {
          ...pageInfo.outreach,
          werknl: {
            email: currentUser.email ?? '',
            phone: currentUser.phone_number ?? '',
            website: currentUser.tenant?.privacy_policy_link ?? '',
          },
        },
      }));
    }
  }, [currentUser]);

  const onContact = async () => {
    let subject = '';
    let message = '';
    let improvedJobPreview: string | undefined;
    let jobTitle = '';
    let aiSimilarityScore: number | undefined = undefined;
    let wasAiMessageModified: boolean | undefined = undefined;

    if (selectedVacancy?.db_id && isFeatureToggleEnabled((f) => f.RJ_COMMUNICATION_HUB)) {
      const {
        job_details: { job_title },
        job_preview,
      } = await useGetJobImprovedQuery.fetchQuery(selectedVacancy.db_id);

      improvedJobPreview = job_preview || undefined;
      jobTitle = job_title || '';
    }

    if (editorSubjectRef) {
      switch (type) {
        case OutreachType.Email:
        case OutreachType.LinkedinInmail:
        case OutreachType.Indeed:
          subject = editorSubjectRef.getTextContent({ replaceTokens: true });
      }
    }

    if (editorMessageRef) {
      switch (type) {
        case OutreachType.Email:
        case OutreachType.LinkedinInmail:
          message = editorMessageRef.serializeToHTML({ replaceTokens: true });
          break;
        case OutreachType.Indeed:
        case OutreachType.Phone:
        case OutreachType.Linkedin:
        case OutreachType.Werknl:
        case OutreachType.Whatsapp:
          message = editorMessageRef.getTextContent({ replaceTokens: true });
      }
    }

    if (
      isRobinJrEnabled &&
      aiOriginalMessage &&
      [OutreachType.Email, OutreachType.LinkedinInmail, OutreachType.Indeed, OutreachType.Whatsapp].includes(type)
    ) {
      const modifiedHtml =
        type === OutreachType.Email || type === OutreachType.LinkedinInmail
          ? editorMessageRef?.serializeToHTML() ?? ''
          : editorMessageRef?.getTextContent({ replaceTokens: true }) ?? '';
      const modifiedPlainText = htmlToPlainText(modifiedHtml);
      const aiOriginalPlainText = htmlToPlainText(aiOriginalMessage);
      aiSimilarityScore = calculateTextSimilarity(aiOriginalPlainText, modifiedPlainText);
      wasAiMessageModified = aiSimilarityScore < AI_MODIFIED_TEXT_SIMILARITY_THRESHOLD;
    }

    switch (type) {
      case OutreachType.Phone:
        await Promise.all([
          candidateNotesMutation.mutateAsync({ note: message, candidate }),
          contactCandidateMutation.mutateAsync({
            ...baseData,
            sendMethod: 'call',
            data: {
              esPersonId: candidate.es_person_id,
              newLabels: ['contacted'],
            } satisfies ContactCandidateData<'call'>,
          }),
        ]);

        EventBus.emit(
          CandidateEvent.LabelsUpdated,
          { esPersonId: candidate.es_person_id, labels: ['contacted'] },
          candidate.es_person_id,
        );

        setPage(OutreachPanelPage.Labels);
        setPageInfo((pageInfo) => ({ ...pageInfo, contact: 'success' }));

        break;

      case OutreachType.Whatsapp:
        if (candidate.not_obfuscated_phone) {
          openWhatsappWeb(candidate.not_obfuscated_phone, message);
          setShowWhatsappWebModal(true);
          return;
        }
        return;

      case OutreachType.Linkedin:
        return contactCandidateMutation
          .mutateAsync({
            ...baseData,
            sendMethod: 'linkedin',
            data: { esPersonId: candidate.es_person_id, message } satisfies ContactCandidateData<'linkedin'>,
          })
          .then(() => candidateOutreachModal.close());

      case OutreachType.LinkedinInmail:
        return contactCandidateMutation
          .mutateAsync({
            ...baseData,
            sendMethod: 'linkedin_inmail',
            data: {
              esPersonId: candidate.es_person_id,
              message,
              subject,
              isAiLeveraged: isRobinJrEnabled && !!aiOriginalMessage,
              ...(isRobinJrEnabled && !!aiOriginalMessage && { outreachOrigin: OutreachOrigin.rjOutreach }),
              ...(aiSimilarityScore !== undefined && { similarity_score: aiSimilarityScore }),
              ...(wasAiMessageModified !== undefined && { was_ai_message_modified: wasAiMessageModified }),
              ...(selectedVacancy?.db_id && { job_improved_id: selectedVacancy.db_id }),
            } satisfies ContactCandidateData<'linkedin_inmail'>,
          })
          .then(() => candidateOutreachModal.close());

      case OutreachType.Email:
        return contactCandidateMutation
          .mutateAsync({
            ...baseData,
            sendMethod: 'email',
            data: {
              esPersonId: candidate.es_person_id,
              message,
              subject,
              signOff: emailSignature.data?.signature_html,
              attachments,
              isAiLeveraged: isRobinJrEnabled && !!aiOriginalMessage,
              ...(isRobinJrEnabled && !!aiOriginalMessage && { outreachOrigin: OutreachOrigin.rjOutreach }),
              shouldScheduleFollowUp,
              ...(aiSimilarityScore !== undefined && { similarity_score: aiSimilarityScore }),
              ...(wasAiMessageModified !== undefined && { was_ai_message_modified: wasAiMessageModified }),
              ...(selectedVacancy?.db_id && { job_improved_id: selectedVacancy.db_id }),
            } satisfies ContactCandidateData<'email'>,
          })
          .then(() => {
            candidateOutreachModal.close();
            setTimeout(() => {
              if (isRobinJrEnabled && aiOriginalMessage) {
                const modifiedHtml = editorMessageRef?.serializeToHTML() ?? '';

                const modifiedPlainText = htmlToPlainText(modifiedHtml);
                const aiOriginalPlainText = htmlToPlainText(aiOriginalMessage);

                const changes = calculateLevenshteinDistance(aiOriginalPlainText, modifiedPlainText);

                const ratio = changes / Math.max(aiOriginalPlainText.length, modifiedPlainText.length);

                gtm.reachoutChangesOnAiMessage({
                  ratio,
                  changes,
                  originalLength: aiOriginalPlainText.length,
                  modifiedLength: modifiedPlainText.length,
                });
                setAiOriginalMessage(undefined);
              }
            }, 0);
          });

      case OutreachType.Indeed:
        return contactCandidateMutation
          .mutateAsync({
            ...baseData,
            sendMethod: 'indeed',
            data: {
              esPersonId: candidate.es_person_id,
              candidateName: candidate.name,
              subject: subject || jobTitle,
              message,
              indeedProjectId: selectedIndeedProject?.key,
              indeedJob: selectedVacancy?.type === 'INDEED' ? selectedVacancy.originalIndeedJob : undefined,
              isReply,
              metadata,
              isAiLeveraged: isRobinJrEnabled && !!aiOriginalMessage,
              ...(isRobinJrEnabled && !!aiOriginalMessage && { outreachOrigin: OutreachOrigin.rjOutreach }),
              improvedJobPreview,
              ...(selectedVacancy?.db_id && { job_improved_id: selectedVacancy.db_id }),
              ...(aiSimilarityScore !== undefined && { similarity_score: aiSimilarityScore }),
              ...(wasAiMessageModified !== undefined && { was_ai_message_modified: wasAiMessageModified }),
            } satisfies ContactCandidateData<'indeed'>,
          })
          .then(() => candidateOutreachModal.close());

      case OutreachType.Werknl:
        return contactCandidateMutation
          .mutateAsync({
            ...baseData,
            sendMethod: 'werknl',
            data: {
              esPersonId: candidate.es_person_id,
              subject,
              message,
              email: pageInfo.outreach.werknl.email,
              phoneNumber: pageInfo.outreach.werknl.phone,
              website: pageInfo.outreach.werknl.website,
            } satisfies ContactCandidateData<'werknl'>,
          })
          .then(() => candidateOutreachModal.close());
    }
  };

  const onRequestChangeType = useCallback((editorType: OutreachType) => {
    setType(editorType);
    setPage(OutreachPanelPage.Outreach);
  }, []);

  const value = {
    refs: {
      subject: editorSubjectRef,
      message: editorMessageRef,
      setMessage: setEditorMessageRef,
      setSubject: setEditorSubjectRef,
    },
    selectedTemplate,
    setSelectedTemplate,
    selectedIndeedProject,
    setSelectedIndeedProject,
    selectedIndeedJob,
    setSelectedIndeedJob,
    isIndeedReply: isReply,
    candidate,
    type,
    page,
    setPage,
    onRequestChangeType,
    onContactSuccesful: onContact,
    onContactUnsuccessful: async () => {
      const message = editorMessageRef?.getTextContent();
      await Promise.all([
        (async () => {
          if (message) {
            await candidateNotesMutation.mutateAsync({ note: message, candidate });
          }
        })(),
        contactCandidateMutation.mutateAsync({
          ...baseData,
          sendMethod: 'call',
          data: {
            esPersonId: candidate.es_person_id,
            newLabels: ['contacted'],
          } satisfies ContactCandidateData<'call'>,
        }),
      ]);

      EventBus.emit(
        CandidateEvent.LabelsUpdated,
        { esPersonId: candidate.es_person_id, labels: ['contacted'] },
        candidate.es_person_id,
      );

      setPage(OutreachPanelPage.Labels);
      setPageInfo((pageInfo) => ({ ...pageInfo, contact: 'unsuccess' }));
    },
    pageInfo,
    setPageInfo,
    attachments,
    setAttachments,
    onSendMessage: async () =>
      await onContact().catch((e) => {
        if (EventBus.isTransactionActive(candidate.es_person_id)) {
          EventBus.clearTransaction(candidate.es_person_id);
        }

        createSnackbar(getIndeedCandidateEngagementErrorMessage(e, dictionary) ?? e?.message ?? '', {
          variant: 'danger',
        });
      }),
    unSubmitedLabels,
    setUnSubmitedLabels,
    errors,
    setErrors,
  } satisfies OutreachModalContextState;

  return (
    <>
      <OutreachModalContext.Provider value={value}>
        <FloatingPortal>
          <motion.div
            id="outreach-modal-root"
            css={classes.root}
            initial={{ width: 0 }}
            animate={{ width: WIDTH_EXPANDED_TOOLBAR }}
            exit={{ width: 0 }}
            transition={{ duration: 0.24, ease: [0.22, 0.9, 0.53, 1] }}>
            <motion.div
              key={type}
              css={classes.fadeInOut}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.24, ease: [0.22, 0.9, 0.53, 1] }}>
              <OutreachPanelLazy />
            </motion.div>
          </motion.div>
        </FloatingPortal>
      </OutreachModalContext.Provider>
      <Dialog
        maxWidth={600}
        isVisible={showWhatsappWebModal}
        title={dictionary.confirmMessageTitleModal}
        body={dictionary.confirmMessageDescriptionModal}
        renderButtons={
          <>
            <Button
              label={dictionary.messageNotSent}
              onClick={() => setShowWhatsappWebModal(false)}
              style="filled"
              type="warning"
              startIcon={XCircle}
            />
            <Button
              label={dictionary.messageSent}
              onClick={() => {
                setIsSendingWhatsappMessage(true);
                const message = editorMessageRef?.getTextContent({ replaceTokens: true }) ?? '';

                contactCandidateMutation
                  .mutateAsync({
                    ...baseData,
                    sendMethod: 'whatsapp',
                    data: {
                      esPersonId: candidate.es_person_id,
                      message,
                      isAiLeveraged: isRobinJrEnabled && !!aiOriginalMessage,
                      ...(isRobinJrEnabled && !!aiOriginalMessage && { outreachOrigin: OutreachOrigin.rjOutreach }),
                      ...(selectedVacancy?.db_id && { job_improved_id: selectedVacancy.db_id }),
                    } satisfies ContactCandidateData<'whatsapp'>,
                  })
                  .finally(() => {
                    setIsSendingWhatsappMessage(false);
                    setShowWhatsappWebModal(false);
                    candidateOutreachModal.close();
                  });
              }}
              style="filled"
              type="highlight"
              startIcon={
                isSendingWhatsappMessage ? <Loader size={18} color={(c) => c.primary.fuchsia} /> : CheckCircleBroken
              }
            />
          </>
        }
      />
    </>
  );
};
